import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { DollarSign, Edit2, Plus, Trash2, X, Save, AlertCircle, Info } from "lucide-react";
import { API } from "@/contexts/AuthContext";

export default function NDISRates() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ndis-rates`);
      setRates(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteRate = async (id) => {
    if (!window.confirm("Delete this rate? Existing tasks will not be affected.")) return;
    try {
      await axios.delete(`${API}/ndis-rates/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  // Group by category
  const grouped = rates.reduce((m, r) => {
    if (!m[r.category]) m[r.category] = [];
    m[r.category].push(r);
    return m;
  }, {});

  return (
    <div data-testid="ndis-rates-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Director only</div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">NDIS rates</h1>
          <p className="text-[#4A595E] mt-2 max-w-2xl">
            The price catalogue used when logging billable tasks. Defaults reflect the 2026-27 NDIS Pricing Arrangements (effective 1 July 2026). Update these when the 2027-28 guide is published in July 2027.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="add-rate-btn">
          <Plus className="w-4 h-4" /> Add custom rate
        </button>
      </div>

      <div className="bg-[#F1E4D5] border border-[#F0E5DE] rounded-2xl p-4 mb-6 flex gap-3" data-testid="rates-info">
        <Info className="w-5 h-5 text-[#EA823A] shrink-0 mt-0.5" />
        <div className="text-sm text-[#284952]">
          <strong>GST-free by default.</strong> NDIS supports are typically GST-free under the GST-free Supply (Care) determinations. Edit a rate to override per item if needed.
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden">
              <div className="px-6 py-4 bg-[#F1E4D5] border-b border-[#F0E5DE]">
                <h2 className="font-display text-lg text-[#1F2A2E]">{category}</h2>
              </div>
              <table className="w-full text-sm">
                <thead className="text-left">
                  <tr>
                    <Th>Code</Th>
                    <Th>Item name</Th>
                    <Th>Rate</Th>
                    <Th>GST</Th>
                    <Th>Active</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0E5DE]">
                  {items.map((r) => (
                    <tr key={r.id} className="hover:bg-[#F1E4D5]/40" data-testid={`rate-row-${r.code}`}>
                      <td className="px-5 py-4 font-mono text-xs text-[#284952]">{r.code}</td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-[#1F2A2E]">{r.name}</div>
                        {r.notes && <div className="text-xs text-[#6B7A80] mt-0.5">{r.notes}</div>}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap font-semibold text-[#1F2A2E]">${Number(r.rate).toFixed(2)} <span className="text-xs text-[#6B7A80]">/ {r.unit}</span></td>
                      <td className="px-5 py-4 text-xs">
                        <span className={`px-2 py-1 rounded-full font-semibold ${r.tax_type === "NONE" ? "bg-[#DCEFE0] text-[#1F6B3A]" : "bg-[#FCEADB] text-[#A0521A]"}`}>
                          {r.tax_type === "NONE" ? "GST-free" : "10% GST"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs">
                        <span className={r.active ? "text-emerald-700" : "text-[#6B7A80]"}>{r.active ? "Active" : "Inactive"}</span>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => { setEditing(r); setShowForm(true); }} className="text-[#284952] hover:text-[#E2718A]" data-testid={`edit-rate-${r.code}`} aria-label="Edit">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteRate(r.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-rate-${r.code}`} aria-label="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <RateFormModal
          rate={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function RateFormModal({ rate, onClose, onSaved }) {
  const isEdit = !!rate?.id;
  const [form, setForm] = useState({
    code: rate?.code || "",
    name: rate?.name || "",
    category: rate?.category || "Support Coordination",
    rate: rate?.rate ?? 0,
    unit: rate?.unit || "hour",
    tax_type: rate?.tax_type || "NONE",
    effective_from: rate?.effective_from || "2025-07-01",
    notes: rate?.notes || "",
    active: rate?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await axios.put(`${API}/ndis-rates/${rate.id}`, form);
      } else {
        await axios.post(`${API}/ndis-rates`, form);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save rate.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="rate-form-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE]">
          <h2 className="font-display text-2xl text-[#1F2A2E]">{isEdit ? "Edit NDIS rate" : "Add NDIS rate"}</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-7 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">NDIS support item code</label>
              <input className="input-navica font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required placeholder="07_002_0106_8_3" data-testid="rf-code" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Category</label>
              <input list="rate-categories" className="input-navica" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="rf-category" />
              <datalist id="rate-categories">
                <option value="Support Coordination" />
                <option value="Recovery Coaching" />
                <option value="Allied Health" />
                <option value="Core, Daily Activities" />
                <option value="Employment Assistance" />
                <option value="Provider Travel" />
                <option value="DVA" />
                <option value="Other" />
              </datalist>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Item name</label>
            <input className="input-navica" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="rf-name" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Rate (AUD)</label>
              <input type="number" step="0.01" min="0" className="input-navica" value={form.rate} onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) || 0 })} required data-testid="rf-rate" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Unit</label>
              <select className="input-navica" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="rf-unit">
                <option value="hour">per hour</option>
                <option value="each">each</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">GST</label>
              <select className="input-navica" value={form.tax_type} onChange={(e) => setForm({ ...form, tax_type: e.target.value })} data-testid="rf-tax">
                <option value="NONE">GST-free</option>
                <option value="GST">10% GST</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Effective from</label>
              <input type="date" className="input-navica" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="rf-effective" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="navica-check" data-testid="rf-active" />
                <span className="text-sm text-[#284952]">Active (appears in task picker)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Notes (optional)</label>
            <textarea rows={2} className="input-navica" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rf-notes" />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F0E5DE]">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="rf-submit">
              {saving ? "Saving..." : "Save rate"} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
